import { isNumberString } from 'lib/detectType'
// crontab 형식의 문자열을 한국어로 변환하는 함수
export function formatCrontabToKorean(cronExpr) {
  const [minute, hour, day, month, weekday] = cronExpr.split(' ')
  const dayMap = {
    0: '일요일',
    1: '월요일',
    2: '화요일',
    3: '수요일',
    4: '목요일',
    5: '금요일',
    6: '토요일',
    7: '일요일'
  }

  const monthMap = {
    1: '1월',
    2: '2월',
    3: '3월',
    4: '4월',
    5: '5월',
    6: '6월',
    7: '7월',
    8: '8월',
    9: '9월',
    10: '10월',
    11: '11월',
    12: '12월'
  }

  const compressRange = (arr, labelMap) => {
    arr = [...new Set(arr.map(Number))].sort((a, b) => a - b)
    const result = []
    let start = arr[0]
    let end = arr[0]

    for (let i = 1; i <= arr.length; i++) {
      if (arr[i] === end + 1) {
        end = arr[i]
      } else {
        if (start === end) {
          result.push(labelMap ? labelMap[start] : `${start}일`)
        } else {
          const startLabel = labelMap ? labelMap[start] : `${start}일`
          const endLabel = labelMap ? labelMap[end] : `${end}일`
          result.push(`${startLabel}~${endLabel}`)
        }
        start = arr[i]
        end = arr[i]
      }
    }

    return result.join(', ')
  }

  const parseField = (field, labelMap) => {
    if (field === '*') return ''
    const parts = field.split(',').flatMap(part =>
      part.includes('-')
        ? (() => {
            const [s, e] = part.split('-').map(Number)
            const res = []
            for (let i = s; i <= e; i++) res.push(i)
            return res
          })()
        : [Number(part)]
    )
    return compressRange(parts, labelMap)
  }

  const formatHour = hourStr => {
    if (!hourStr.includes('-')) return ''
    const [start, end] = hourStr.split('-').map(Number)
    const format = h =>
      h < 12 ? `오전 ${h}시` : h === 12 ? `오후 12시` : `오후 ${h - 12}시`
    return `${format(start)} ~ ${format(end)} 사이`
  }

  const parts = []

  // 월
  const monthStr = parseField(month, monthMap)
  if (monthStr) parts.push(`${monthStr}에`)

  // 일
  const dayStr = parseField(day, null)
  if (dayStr) parts.push(`${dayStr}에`)

  // 요일
  const weekdayStr = parseField(weekday, dayMap)
  if (weekdayStr) parts.push(`매주 ${weekdayStr}에`)

  // 시
  const hourStr = formatHour(hour)
  if (hourStr) parts.push(hourStr)

  // 분
  const minuteStr = minute === '*' ? '1분 주기로' : `${minute}분마다`
  parts.push(`${minuteStr} 실행합니다.`)

  return parts.join('\n')
}

export function parseCrontabToKorean(cron) {
  if (!cron) return ''
  const [min, hour, day, month, weekday] = cron.trim().split(' ')

  const weekDays = {
    0: '일요일',
    1: '월요일',
    2: '화요일',
    3: '수요일',
    4: '목요일',
    5: '금요일',
    6: '토요일',
    7: '일요일'
  }

  function formatWeekdays(field) {
    if (field === '*') return '매일'
    const parts = field.split(',')
    const items = []

    for (const part of parts) {
      if (part.includes('-')) {
        const [start, end] = part.split('-').map(Number)
        items.push(`${weekDays[start]} ~ ${weekDays[end]}`)
      } else {
        items.push(weekDays[parseInt(part, 10)])
      }
    }

    return `매주 ${items.join(', ')}`
  }

  function getHourLabel(h) {
    const hour = parseInt(h, 10)
    if (isNaN(hour)) return '매시간'
    if (hour === 0) return `오전 12시(자정)`
    if (hour >= 0 && hour <= 5) return `새벽 ${hour}시`
    if (hour >= 6 && hour <= 11) return `오전 ${hour}시`
    if (hour === 12) return `오후 12시(정오)`
    if (hour >= 13 && hour <= 17) return `오후 ${hour - 12}시`
    if (hour >= 18 && hour <= 23) return `밤 ${hour - 12}시`
    return `${hour}시`
  }

  function getHourText(hour) {
    if (hour === '*' || hour === '*/1') return '매시간'

    // 콤마로 분리된 복수 범위 또는 값 처리
    if (hour.includes(',')) {
      const parts = hour.split(',')
      return parts
        .map(part => getHourText(part)) // 재귀적으로 처리
        .join(', ')
    }

    if (hour.includes('/')) {
      const [range, step] = hour.split('/')
      if (range.includes('-')) {
        const [start, end] = range.split('-').map(Number)
        return `${getHourLabel(start)} ~ ${getHourLabel(end)} ${step}시 주기`
      }
      return `${step}시 주기`
    }

    if (hour.includes('-')) {
      const [start, end] = hour.split('-').map(Number)
      return `${getHourLabel(start)} ~ ${getHourLabel(end)}`
    }

    return getHourLabel(hour)
  }

  function parseField(field, type, dict = null, joinPrefix = '') {
    if (field === '*') return `매${type}`
    if (field.includes('/')) return `${field.split('/')[1]}마다 ${type}`
    if (field.includes(',')) {
      const parts = field.split(',')
      const values = parts.map(f =>
        parseField(f, type, dict).replace(/^매주\s*/, '')
      )
      return `${joinPrefix}${values.join(', ')}`
    }
    if (field.includes('-')) {
      const [start, end] = field.split('-')
      if (dict) return `${type} ${dict[start]}~${dict[end]}`
      return `${type} ${start}~${end}`
    }
    return dict ? `${joinPrefix}${dict[field]}` : `${field}${type}`
  }

  const minuteText =
    min === '*' || min === '*/1'
      ? '매분'
      : min.includes('/')
      ? `${min.split('/')[1]}분 주기`
      : min === '0'
      ? '정각'
      : `${parseInt(min)}분`

  const hourText = getHourText(hour)
  const dayText = day === 'L' ? '달의 마지막 날' : parseField(day, '일')
  const monthText = parseField(month, '월')
  const weekdayText = weekday === '*' ? '매일' : formatWeekdays(weekday)

  let result = ''
  if (monthText !== '매월') result += `${monthText} `
  if (dayText !== '매일') result += `${dayText} `
  if (weekdayText !== '매일') result += `${weekdayText} `
  if (hourText === '매시간' && minuteText === '매분') {
    result += `${minuteText}`
  } else {
    result += `${hourText} ${minuteText}`
  }
  return result.trim()
}

// 숫자 범위를 압축하는 함수 [1,3,4] -> "1,3-4"
export function compressNumberRanges(numbers) {
  if (numbers?.length === 7) {
    return '*'
  }
  const sorted = [...new Set(numbers)].sort((a, b) => a - b)
  const ranges = []

  let start = sorted[0]
  let end = sorted[0]

  for (let i = 1; i <= sorted.length; i++) {
    if (sorted[i] === end + 1) {
      end = sorted[i]
    } else {
      if (start === end) {
        ranges.push(`${start}`)
      } else if (end === start + 1) {
        ranges.push(`${start},${end}`)
      } else {
        ranges.push(`${start}-${end}`)
      }
      start = sorted[i]
      end = sorted[i]
    }
  }

  return ranges.join(',')
}

// 숫자 범위를 확장하는 함수 "1,3-5" -> [1, 3, 4, 5]
export function expandNumberRanges(rangeStr) {
  if (rangeStr === '*') {
    return [0, 1, 2, 3, 4, 5, 6]
  }
  const result = []
  const parts = rangeStr.split(',')
  for (const part of parts) {
    if (part.includes('-')) {
      const [start, end] = part.split('-').map(Number)
      for (let i = start; i <= end; i++) {
        result.push(i)
      }
    } else {
      result.push(Number(part))
    }
  }

  return result
}

// crontab 형식의 문자열에서 시간 범위를 시작시간, 종료시간으로 파싱하는 함수
export function parseHourRange(hourRange) {
  if (hourRange === '*') {
    return {}
  }
  // 예: "0-7,18-23"
  if (hourRange.includes(',')) {
    const parts = hourRange.split(',')
    const [ranges1] = parts[0].split('/')
    const [ranges2] = parts[1].split('/')
    const firstRange = ranges1.split('-').map(Number)
    const secondRange = ranges2.split('-').map(Number)
    // 이 경우 startHour는 firstRange[1], endHour는 secondRange[0]
    const endHour = firstRange[1]
    const startHour = secondRange[0]
    if (!isNumberString(startHour) || !isNumberString(endHour)) {
      return false
    }
    return { startHour, endHour }
  } else {
    // 예: "18-7 18-7/1"
    const [ranges] = hourRange.split('/')
    const [startHour, endHour] = ranges.split('-').map(Number)
    if (!isNumberString(startHour) || !isNumberString(endHour)) {
      return false
    }
    return { startHour, endHour }
  }
}

// 시작 시각과 종료 시각을 받아 crontab 형식의 문자열을 생성하는 함수
export function generateSingleCrontabHourRange(startHour, endHour) {
  const hourRange =
    startHour > endHour
      ? `0-${endHour},${startHour}-23`
      : `${startHour}-${endHour}`
  return hourRange
}

export default {
  formatCrontabToKorean,
  compressNumberRanges,
  expandNumberRanges,
  parseHourRange,
  generateSingleCrontabHourRange,
  parseCrontabToKorean
}
